import React, { useCallback, useEffect, useMemo, useRef } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isPercentageComplete,
  isValidPercentageDecimal,
  isValidPositiveNumber,
  useGetBeefFeedTypes,
  useGetFootprintingBeefFeedInformation,
  useGetFootprintingBeefHerdSize,
  useGetFootprintingBeefPrimaryDataById,
  useKeyedFormList,
  useMutateFootprintingBeefFeedInformation,
  useSaveFormOnUnmount,
  useTranslation,
} from "utils";

import { DEFAULT_DRY_MATTER_PERCENTAGE, KG_PER_TONNE } from "consts";

import { BeefFeedType, DAIRY_FEED_ENERGY_UNIT, ENUM_DAIRY_FEED_ENERGY_UNIT, UploadableBeefFeedInformation, WithInjectedName } from "models";

import {
  AlertBanner,
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputImageCard,
  InputNumber,
  InputNumberWithUnitSelector,
  Option,
  PageSpin,
  ProgressBarCardWithContent,
  Select,
  Switch,
  Typography,
} from "components";

type BeefFeedInformationFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const STANDARD_FEED_ENERGY_CONTENT_MJNEL = 7;
const CORRECTED_ENERGY_CONSTANT = KG_PER_TONNE / STANDARD_FEED_ENERGY_CONTENT_MJNEL;

const createFeedTypePlaceholder = (): UploadableBeefFeedInformation => ({
  id: undefined,
  feedTypeId: "",
  isSelfProduced: null,
  totalAmountFedTs: null,
  silagePct: null,
  energyValue: null,
  energyUnit: DAIRY_FEED_ENERGY_UNIT.MJNEL,
  dryMatterPct: null,
  cowsPct: null,
  calvesPct: null,
  emissionFactorKgCo2Eq: null,
});

const getEnergyValuesFromFeedType = (feedType: WithInjectedName<BeefFeedType> | undefined) => {
  if (!feedType) return { energyValue: null, energyUnit: null };
  if (feedType.energyMjNelKgDm) return { energyValue: feedType.energyMjNelKgDm, energyUnit: DAIRY_FEED_ENERGY_UNIT.MJNEL };
  if (feedType.energyMjMeKgDm) return { energyValue: feedType.energyMjMeKgDm, energyUnit: DAIRY_FEED_ENERGY_UNIT.MJME };
  return { energyValue: null, energyUnit: null };
};

const BeefFeedInformationForm = ({ primaryDataId, readOnly, farmId, year }: BeefFeedInformationFormProps) => {
  const { t } = useTranslation("footprintingBeef");
  const formRef = useRef<FormInstance>(null);

  const { data: feedInformationQuery, isLoading } = useGetFootprintingBeefFeedInformation(primaryDataId ?? "");
  const { data: beefFeedTypesQuery } = useGetBeefFeedTypes();
  const { data: primaryData } = useGetFootprintingBeefPrimaryDataById(primaryDataId ?? "");
  const { data: herdSize } = useGetFootprintingBeefHerdSize(primaryDataId ?? "");

  const keyedFeedInformations = useKeyedFormList<UploadableBeefFeedInformation>(formRef, { isReady: !isLoading });

  const feedInformationsPayloadRef = useRef(keyedFeedInformations.payload);

  useEffect(() => {
    feedInformationsPayloadRef.current = keyedFeedInformations.payload;
  }, [keyedFeedInformations.payload]);

  const selectGroupName = "footprinting-beef-feed-crop-select";

  const { mutate: mutateFeedInformation } = useMutateFootprintingBeefFeedInformation(primaryDataId ?? "", farmId, year);

  const saveFeedInformation = useCallback(
    () => mutateFeedInformation(feedInformationsPayloadRef.current.filter((f) => !!f.feedTypeId)),
    [mutateFeedInformation]
  );
  const markFormPopulated = useSaveFormOnUnmount(saveFeedInformation, !readOnly && !isLoading, !primaryDataId);

  useEffect(() => {
    if (keyedFeedInformations.entries.length === 0 || keyedFeedInformations.entries.some((f) => !f.id)) return;
    formRef.current?.validateFields();
  }, [keyedFeedInformations.entries]);

  useEffect(() => {
    if (!feedInformationQuery || !beefFeedTypesQuery) {
      return;
    }

    const newFeedTypes: UploadableBeefFeedInformation[] = feedInformationQuery.map((f) => ({
      id: f.id,
      feedTypeId: f.feedTypeId ?? "",
      isSelfProduced: f.isSelfProduced ?? null,
      totalAmountFedTs: f.totalAmountFedTs ?? null,
      silagePct: f.silagePct ?? null,
      energyValue: f.energyValue ?? null,
      energyUnit: f.energyUnit ?? null,
      dryMatterPct: f.dryMatterPct ?? null,
      cowsPct: f.cowsPct ?? null,
      calvesPct: f.calvesPct ?? null,
      emissionFactorKgCo2Eq: f.emissionFactorKgCo2Eq ?? null,
    }));

    markFormPopulated();

    // Each entry mounts its fields fresh under its own `clientKey`, so `initialValue` seeds the form without a manual sync

    keyedFeedInformations.actions.hydrate(newFeedTypes.length > 0 ? newFeedTypes : [createFeedTypePlaceholder()]);
  }, [feedInformationQuery, beefFeedTypesQuery, markFormPopulated, keyedFeedInformations.actions]);

  const isAnyFeedTypeSilage = keyedFeedInformations.entries.some((f) => beefFeedTypesQuery?.find((ft) => ft.id === f.feedTypeId)?.isSilage ?? false);

  const feedValidation = useMemo(() => {
    const totalCowCount = herdSize?.reduce((sum, herd) => sum + herd.cowCount, 0) ?? 0;
    const beefYieldKg = primaryData?.beefYieldKg;

    if (!totalCowCount || !beefYieldKg) {
      return { energyMjNelPerCow: 0, isTooHigh: false, isTooLow: false };
    }

    const validFeeds = keyedFeedInformations.entries.filter(
      (f) =>
        f.totalAmountFedTs !== null &&
        f.energyValue !== null &&
        f.dryMatterPct !== null &&
        f.feedTypeId &&
        beefFeedTypesQuery?.find((ft) => ft.id === f.feedTypeId && !ft.isSilage)
    );

    if (validFeeds.length === 0) {
      return { energyMjNelPerCow: 0, isTooHigh: false, isTooLow: false };
    }

    const energyCorrected = validFeeds.reduce((totalEnergy, feed) => {
      const amount = feed.totalAmountFedTs ?? 0;
      const dryMatterFraction = feed.dryMatterPct ?? DEFAULT_DRY_MATTER_PERCENTAGE;
      let energyInNel = feed.energyValue ?? 0;

      if (feed.energyUnit === DAIRY_FEED_ENERGY_UNIT.MJME) {
        const feedType = beefFeedTypesQuery?.find((ft) => ft.id === feed.feedTypeId);
        if (feedType?.mjMEtoMJNEL) energyInNel *= feedType.mjMEtoMJNEL;
      }

      return totalEnergy + amount * dryMatterFraction * energyInNel;
    }, 0);

    const energyCorrectedPerCow = (energyCorrected * CORRECTED_ENERGY_CONSTANT) / totalCowCount;
    const efficiency = energyCorrectedPerCow / beefYieldKg;

    return {
      energyMjNelPerCow: Math.round(energyCorrectedPerCow * STANDARD_FEED_ENERGY_CONTENT_MJNEL),
      isTooHigh: efficiency > 0.5,
      isTooLow: efficiency < 0.1,
      isInRange: efficiency >= 0.1 && efficiency <= 0.5,
    };
  }, [keyedFeedInformations.entries, primaryData, herdSize, beefFeedTypesQuery]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Flex vertical gap={16}>
      <Card dataTestId="footprinting-beef-feed-information-form">
        <Form ref={formRef} onValuesChange={markFormPopulated}>
          <Flex vertical gap={16}>
            <Flex vertical className="padding-100 padding-left-0" gap={8}>
              <Typography variant="h3">{t("beefFeedInformationForm.header")}</Typography>
              <Typography variant="paragraph-m" color="grey-600" className="margin-0">
                {t("beefFeedInformationForm.description")}
              </Typography>
            </Flex>

            <Flex vertical gap={16}>
              {keyedFeedInformations.entries.map((feedInfo, index) => {
                const selectGroupWithId = `${selectGroupName}-${index}`;
                const { clientKey } = feedInfo;
                const updateItem = (item: Partial<UploadableBeefFeedInformation>) => {
                  keyedFeedInformations.actions.update(clientKey, item);
                };

                const handleFeedDistributionChange = (value: number | null, target: "cows" | "calves") => {
                  const pct = formatPercentageInput(value);

                  if (pct === null) {
                    updateItem({ ...feedInfo, cowsPct: null, calvesPct: null });
                    formRef.current?.setFieldValue(`cowsPct-${clientKey}`, null);
                    formRef.current?.setFieldValue(`calvesPct-${clientKey}`, null);
                    return;
                  }

                  if (target === "cows") {
                    updateItem({ ...feedInfo, cowsPct: pct, calvesPct: 1 - pct });
                    formRef.current?.setFieldValue(`cowsPct-${clientKey}`, formatFractionInput(pct));
                    formRef.current?.setFieldValue(`calvesPct-${clientKey}`, formatFractionInput(1 - pct));
                  } else {
                    updateItem({ ...feedInfo, calvesPct: pct, cowsPct: 1 - pct });
                    formRef.current?.setFieldValue(`cowsPct-${clientKey}`, formatFractionInput(1 - pct));
                    formRef.current?.setFieldValue(`calvesPct-${clientKey}`, formatFractionInput(pct));
                  }
                };

                const selectedFeedType = beefFeedTypesQuery?.find((ft) => ft.id === feedInfo.feedTypeId);
                const isSilage = selectedFeedType?.isSilage ?? false;

                return (
                  <Card key={clientKey} className="feed-cards padding-200">
                    <Flex vertical gap={16}>
                      <Flex justify="space-between" align="center" vertical={false}>
                        <Typography variant="h5" style={{ flex: 1 }}>
                          {selectedFeedType
                            ? t("beefFeedInformationForm.feedTypeCount", { count: index + 1, cropName: selectedFeedType.name })
                            : t("beefFeedInformationForm.feedTypeCountNoName", { count: index + 1 })}
                        </Typography>
                        <IconButton
                          type="tertiary"
                          disabled={readOnly}
                          dataTestId={`delete-beef-feed-crop-${index}`}
                          size="small"
                          iconName="tabler/trash"
                          onClick={() => keyedFeedInformations.actions.remove(clientKey)}
                        />
                      </Flex>

                      <Flex gap={16} align="center">
                        <Form.Item className="flex-1">
                          <Select
                            showSearch
                            disabled={readOnly}
                            name={`${selectGroupName}-${clientKey}`}
                            dataTestId={selectGroupWithId}
                            label={t("beefFeedInformationForm.question.feedType")}
                            rules={[{ required: true, message: "" }]}
                            initialValue={feedInfo.feedTypeId ?? ""}
                            value={feedInfo.feedTypeId ?? ""}
                            onChange={(selectedFeedId) => {
                              const feedType = beefFeedTypesQuery?.find((ft) => ft.id === selectedFeedId);
                              const { energyValue, energyUnit } = getEnergyValuesFromFeedType(feedType);
                              updateItem({
                                ...feedInfo,
                                feedTypeId: feedType?.id ?? "",
                                energyValue,
                                energyUnit,
                                silagePct: null,
                                dryMatterPct: feedType?.dryMatterPerFeedTypePct ?? null,
                                totalAmountFedTs: feedType?.isSilage ? 0 : feedInfo.totalAmountFedTs,
                              });
                              formRef.current?.setFieldsValue({
                                [`energyValue-${clientKey}`]: energyValue,
                                [`dryMatterPct-${clientKey}`]: formatFractionInput(feedType?.dryMatterPerFeedTypePct ?? null),
                                [`silagePct-${clientKey}`]: null,
                                [`totalAmountFed-${clientKey}`]: feedType?.isSilage ? 0 : feedInfo.totalAmountFedTs,
                              });
                            }}
                            dropdownStyle={{ zIndex: 10010 }}
                          >
                            {(beefFeedTypesQuery ?? []).map((feedType) => {
                              const isAlreadyUsed = keyedFeedInformations.entries.some(
                                (f) => f.feedTypeId === feedType.id && f.feedTypeId !== feedInfo.feedTypeId
                              );
                              return (
                                <Option
                                  disabled={readOnly || isAlreadyUsed}
                                  key={`${selectGroupWithId}-${feedType.id}`}
                                  data-testid={`${selectGroupWithId}-${feedType.id}`}
                                  value={feedType.id}
                                >
                                  {feedType.name}
                                </Option>
                              );
                            })}
                          </Select>
                        </Form.Item>

                        <Flex vertical align="center" gap={8}>
                          <Typography variant="paragraph-s" className="margin-0">
                            {t("beefFeedInformationForm.question.isSelfProduced")}
                          </Typography>
                          <Switch
                            disabled={readOnly}
                            value={feedInfo.isSelfProduced ?? false}
                            onChange={(v) => updateItem({ ...feedInfo, isSelfProduced: v })}
                          />
                        </Flex>
                      </Flex>

                      {isSilage ? (
                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-beef-feed-silage-pct-${index}`}
                          name={`silagePct-${clientKey}`}
                          label={t("beefFeedInformationForm.question.silagePct")}
                          suffix="%"
                          value={formatFractionInput(feedInfo.silagePct ?? null)}
                          initialValue={formatFractionInput(feedInfo.silagePct ?? null)}
                          onChange={(v) => {
                            updateItem({ ...feedInfo, silagePct: formatPercentageInput(v) });
                            setTimeout(() => {
                              keyedFeedInformations.entries.forEach((f) => {
                                if (beefFeedTypesQuery?.find((ft) => ft.id === f.feedTypeId)?.isSilage) {
                                  formRef.current?.validateFields([`silagePct-${f.clientKey}`]);
                                }
                              });
                            }, 0);
                          }}
                          precision={2}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                  return Promise.reject(new Error(t("beefFeedInformationForm.error.validPercentage")));
                                }
                                const total = keyedFeedInformations.entries.reduce((sum, f, i) => {
                                  const value = i === index ? formatPercentageInput(v) ?? 0 : f.silagePct ?? 0;
                                  return sum + value;
                                }, 0);
                                if (Math.round(total * 100) > 100) {
                                  return Promise.reject(new Error(t("beefFeedInformationForm.error.combinedSilagePercentage")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      ) : (
                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-beef-feed-amount-input-${index}`}
                          name={`totalAmountFed-${clientKey}`}
                          label={t("beefFeedInformationForm.question.totalAmountFed")}
                          suffix={t("beefFeedInformationForm.question.totalAmountFedUnit")}
                          value={feedInfo.totalAmountFedTs}
                          initialValue={feedInfo.totalAmountFedTs}
                          onChange={(v) => updateItem({ ...feedInfo, totalAmountFedTs: v })}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPositiveNumber(v)) {
                                  return Promise.reject(new Error(t("beefFeedInformationForm.error.validAmount")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      )}

                      <Typography variant="h6">{t("beefFeedInformationForm.basicInformation")}</Typography>

                      <InputNumberWithUnitSelector
                        disabled={readOnly}
                        dataTestId={`footprinting-beef-feed-energy-input-${index}`}
                        name={`energyValue-${clientKey}`}
                        label={t("beefFeedInformationForm.question.energyValue")}
                        unitLabel={t("unit", { ns: "footprinting" })}
                        units={Object.values(DAIRY_FEED_ENERGY_UNIT)}
                        value={feedInfo.energyValue}
                        initialValue={feedInfo.energyValue}
                        unitValue={feedInfo.energyUnit}
                        onChange={(v, unit) => updateItem({ ...feedInfo, energyValue: v, energyUnit: unit as ENUM_DAIRY_FEED_ENERGY_UNIT })}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPositiveNumber(v)) {
                                return Promise.reject(new Error(t("beefFeedInformationForm.error.validNumber")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />

                      <InputNumber
                        disabled={readOnly}
                        dataTestId={`footprinting-beef-feed-drymatter-input-${index}`}
                        name={`dryMatterPct-${clientKey}`}
                        label={t("beefFeedInformationForm.question.dryMatterPct")}
                        suffix="%"
                        value={formatFractionInput(feedInfo.dryMatterPct ?? null)}
                        initialValue={formatFractionInput(feedInfo.dryMatterPct ?? null)}
                        onChange={(v) => updateItem({ ...feedInfo, dryMatterPct: formatPercentageInput(v) })}
                        precision={2}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("beefFeedInformationForm.error.validPercentage")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />

                      <Flex vertical>
                        <Typography variant="h6">{t("beefFeedInformationForm.feedDistribution")}</Typography>
                        <Typography variant="paragraph-m" color="grey-600" className="margin-0">
                          {t("beefFeedInformationForm.feedDistributionDescription")}
                        </Typography>
                      </Flex>

                      <InputImageCard
                        disabled={readOnly}
                        title={t("beefFeedInformationForm.question.cowsPct")}
                        tooltip={t("beefFeedInformationForm.tooltip.cowsPct")}
                        suffix="%"
                        name={`cowsPct-${clientKey}`}
                        value={formatFractionInput(feedInfo.cowsPct ?? null)}
                        initialValue={formatFractionInput(feedInfo.cowsPct ?? null)}
                        onChange={(v) => {
                          handleFeedDistributionChange(v, "cows");
                          setTimeout(() => {
                            keyedFeedInformations.entries.forEach((f) => {
                              formRef.current?.validateFields([`cowsPct-${f.clientKey}`, `calvesPct-${f.clientKey}`]);
                            });
                          }, 0);
                        }}
                        precision={2}
                        image={{
                          imageAlt: "Cow image",
                          imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/mixed_breed_meat.png",
                        }}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject();
                              }
                              if (v / 100 + (feedInfo.calvesPct ?? 0) > 1) {
                                return Promise.reject();
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />

                      <InputImageCard
                        disabled={readOnly}
                        title={t("beefFeedInformationForm.question.calvesPct")}
                        tooltip={t("beefFeedInformationForm.tooltip.calvesPct")}
                        suffix="%"
                        name={`calvesPct-${clientKey}`}
                        value={formatFractionInput(feedInfo.calvesPct ?? null)}
                        initialValue={formatFractionInput(feedInfo.calvesPct ?? null)}
                        onChange={(v) => {
                          handleFeedDistributionChange(v, "calves");
                          setTimeout(() => {
                            keyedFeedInformations.entries.forEach((f) => {
                              formRef.current?.validateFields([`cowsPct-${f.clientKey}`, `calvesPct-${f.clientKey}`]);
                            });
                          }, 0);
                        }}
                        precision={2}
                        image={{
                          imageSize: 48,
                          imageAlt: "Calves image",
                          imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/calve.png",
                        }}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject();
                              }
                              if (v / 100 + (feedInfo.cowsPct ?? 0) > 1) {
                                return Promise.reject();
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />

                      {feedInfo.cowsPct === null ||
                      feedInfo.calvesPct === null ||
                      isPercentageComplete((feedInfo.cowsPct ?? 0) + (feedInfo.calvesPct ?? 0)) ? null : (
                        <AlertBanner
                          type="error"
                          iconName="tabler/alert-triangle"
                          iconAlign="start"
                          iconSize={24}
                          gap={4}
                          headingSize="h6"
                          description={t("beefFeedInformationForm.error.invalidDistributionDescription")}
                          title={
                            (feedInfo.cowsPct ?? 0) + (feedInfo.calvesPct ?? 0) > 1
                              ? t("beefFeedInformationForm.error.invalidDistributionTitleTooHigh")
                              : t("beefFeedInformationForm.error.invalidDistributionTitleTooLow")
                          }
                        />
                      )}
                    </Flex>
                  </Card>
                );
              })}
            </Flex>

            {isAnyFeedTypeSilage && (
              <ProgressBarCardWithContent
                currentValue={keyedFeedInformations.entries.filter((fi) => fi.feedTypeId).reduce((sum, f) => sum + (f.silagePct ?? 0) * 100, 0)}
                title={t("beefFeedInformationForm.silagePercentageSum.title")}
                showPercentValue={true}
                showRemainingPercentage={true}
                onAnimationFinish={() => {}}
                errorMessage={t("beefFeedInformationForm.silagePercentageSum.errorMessage")}
                successMessage={t("beefFeedInformationForm.silagePercentageSum.successMessage")}
                infoMessage={t("beefFeedInformationForm.silagePercentageSum.description")}
              />
            )}

            <Button
              type="text"
              dataTestId="add-beef-feed-crop-button"
              disabled={readOnly}
              onClick={() => keyedFeedInformations.actions.add(createFeedTypePlaceholder())}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("beefFeedInformationForm.addFeedType")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card border dataTestId="footprinting-beef-feed-energy-check-card" className="padding-200">
        <Flex vertical gap={16}>
          <Flex vertical className="padding-bottom-0" gap={8}>
            <Typography variant="h3">{t("beefFeedInformationForm.feedEnergyTitle")}</Typography>
            <Typography variant="paragraph-m" color="grey-600">
              {t("beefFeedInformationForm.feedEnergyDescription")}
            </Typography>
          </Flex>

          <Card border className="dairy-display-information-card padding-100 flex-center">
            <Flex vertical gap={4}>
              <Typography variant="paragraph-s" color="grey-600" className="center">
                {t("beefFeedInformationForm.feedEnergyDisplayTitle")}
              </Typography>
              <Typography variant="h5" className="center">
                {feedValidation.energyMjNelPerCow} MJ NEL / {t("beefFeedInformationForm.feedEnergyDisplayCowUnit")}
              </Typography>
            </Flex>
          </Card>

          {(feedValidation.isTooHigh || feedValidation.isTooLow || feedValidation.isInRange) && (
            <AlertBanner
              type={feedValidation.isInRange ? "success" : "warning"}
              iconName={feedValidation.isInRange ? "tabler/confetti" : "tabler/info-circle"}
              iconAlign="start"
              iconSize={24}
              headingSize="h6"
              title={
                feedValidation.isTooHigh
                  ? t("beefFeedInformationForm.error.feedAmountTooHigh")
                  : feedValidation.isTooLow
                  ? t("beefFeedInformationForm.error.feedAmountTooLow")
                  : t("beefFeedInformationForm.success.feedAmountInRange")
              }
              description={
                feedValidation.isTooHigh
                  ? t("beefFeedInformationForm.error.feedValidationDescriptionHigh")
                  : feedValidation.isTooLow
                  ? t("beefFeedInformationForm.error.feedValidationDescriptionLow")
                  : t("beefFeedInformationForm.success.feedValidationDescriptionInRange")
              }
            />
          )}
        </Flex>
      </Card>
    </Flex>
  );
};

BeefFeedInformationForm.Key = "beef.feedInformation";
export { BeefFeedInformationForm };
